import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { I18n, Translate } from 'common'
import styled from 'styled-components'
import { VehicleMakes } from '../../../../features/vehicleFitment/components/VehicleMakes'
import { ReactSelect } from '../../../../ui-components/forms/ReactSelect'
import { breakpoints } from '../../../../theme/breakpoints'
import { ProductInformationProps } from 'results'
import CatalogServiceProvider from '../../../../services/CatalogServiceProvider'
import {
  PartVehicles,
  PartVehiclesFitmentRequest,
} from '../../../../store/models/SearchModels'
import { IdValueGeneric, IdValuePair } from 'interfaces'

export const VehicleFitment = observer(
  ({ selectedProduct }: ProductInformationProps): ReactElement => {
    const [selectedManufacturer, setSelectedManufacturer] = useState(null)
    const [selectedGroup, setSelectedGroup] = useState(null)

    const [vehicleFitmentLoading, setVehicleFitmentLoading] =
      useState<boolean>(false)

    const [vehicleFitmentResults, setVehicleFitmentResults] =
      useState<PartVehicles>({
        groups: [],
        manufacturers: [],
        vehicles: [],
      })

    const resetVehicleFitmentResults = useCallback(() => {
      setVehicleFitmentResults({
        groups: [],
        manufacturers: [],
        vehicles: [],
      })
    }, [])

    const searchVehiclesByManufactureAndGroup = useCallback(
      async (requestData: {
        partNumber: string
        manufacturerName: string
        group?: {
          id: number
          value: string
        }
      }): Promise<void> => {
        const reqData: PartVehiclesFitmentRequest = {
          ...requestData,
          manufacturerName: requestData.manufacturerName || '',
        }
        setVehicleFitmentLoading(true)
        try {
          const results =
            await CatalogServiceProvider.getPartVehiclesFitmentInterchange(
              reqData
            )
          setVehicleFitmentResults(results)
          return
        } catch (_e) {
          resetVehicleFitmentResults()
        } finally {
          setVehicleFitmentLoading(false)
        }
      },
      [resetVehicleFitmentResults]
    )

    const handleManufacturers = useCallback(
      (manufacturer) => {
        if (!manufacturer || !selectedProduct) {
          return
        }
        setSelectedManufacturer(manufacturer)
        searchVehiclesByManufactureAndGroup({
          partNumber: selectedProduct.partNumber,
          manufacturerName: manufacturer.value,
          group: {
            id: selectedGroup?.id ? selectedGroup?.id : 0,
            value: selectedGroup?.value ? selectedGroup?.value : '',
          },
        })
      },
      [searchVehiclesByManufactureAndGroup, selectedGroup, selectedProduct]
    )

    const handleGroups = (group) => {
      setSelectedGroup(group)
      searchVehiclesByManufactureAndGroup({
        partNumber: selectedProduct?.partNumber,
        manufacturerName: selectedManufacturer?.value
          ? selectedManufacturer?.value
          : vehicleFitmentResults.manufacturers[0]?.value,
        group: {
          id: group?.id ? group?.id : 0,
          value: group?.value ? group?.value : '',
        },
      })
    }

    useEffect(() => {
      if (!selectedProduct) {
        return
      }
      searchVehiclesByManufactureAndGroup({
        partNumber: selectedProduct.partNumber,
        manufacturerName: selectedProduct.manufacturerName,
        group: {
          id: selectedProduct.group?.id ? Number(selectedProduct.group?.id) : 0,
          value: null,
        },
      })
    }, [searchVehiclesByManufactureAndGroup, selectedProduct])

    useEffect(() => {
      if (
        vehicleFitmentResults &&
        !selectedManufacturer &&
        vehicleFitmentResults.manufacturers.length > 0
      ) {
        const productManufacturer = vehicleFitmentResults.manufacturers.find(
          (manufacturer) =>
            manufacturer &&
            selectedProduct &&
            manufacturer.value.toLowerCase() ===
              selectedProduct.manufacturerName?.toLowerCase()
        )

        let grouptoSelect: IdValuePair<string>

        if (vehicleFitmentResults?.groups.length === 1) {
          grouptoSelect = vehicleFitmentResults.groups[0]
        } else {
          grouptoSelect = (vehicleFitmentResults?.groups || []).find(
            (group) => group.id === selectedProduct.group?.id
          )
        }

        setSelectedManufacturer(
          productManufacturer || vehicleFitmentResults.manufacturers[0]
        )
        if (grouptoSelect) {
          setSelectedGroup(grouptoSelect)
        } else {
          setSelectedGroup({ id: 0, vaue: null })
        }
      }
    }, [selectedManufacturer, selectedProduct, vehicleFitmentResults])

    return (
      <>
        <SelectsWrapper>
          {selectedManufacturer &&
            vehicleFitmentResults.manufacturers.length > 0 &&
            vehicleFitmentResults?.groups?.length > 0 && (
              <SelectWrapper>
                <SelectsLabels> {Translate('manufacturers')}</SelectsLabels>
                <div
                  style={{
                    width: '200px',
                  }}
                >
                  <ReactSelect
                    options={vehicleFitmentResults.manufacturers}
                    defaultValue={selectedManufacturer}
                    value={selectedManufacturer}
                    onChange={handleManufacturers}
                    getOptionLabel={(option: unknown) =>
                      (option as IdValueGeneric<number, string>).value
                    }
                    getOptionValue={(option: unknown) =>
                      (option as IdValueGeneric<number, string>).id.toString()
                    }
                  />
                </div>
              </SelectWrapper>
            )}
          {vehicleFitmentResults?.groups?.length > 0 && (
            <SelectWrapper>
              <SelectsLabels>{Translate('groups')}</SelectsLabels>
              <div
                style={{
                  width: '257px',
                }}
              >
                <ReactSelect
                  options={vehicleFitmentResults?.groups}
                  defaultValue={selectedGroup}
                  value={
                    selectedGroup && selectedGroup.id !== 0
                      ? selectedGroup
                      : undefined
                  }
                  onChange={handleGroups}
                  getOptionLabel={(option: unknown) =>
                    (option as IdValueGeneric<number, string>).value
                  }
                  getOptionValue={(option: unknown) =>
                    (option as IdValueGeneric<number, string>).id.toString()
                  }
                />
              </div>
            </SelectWrapper>
          )}
        </SelectsWrapper>
        <FitmentSpinner>
          {vehicleFitmentLoading && <Spinner animation="border" />}
        </FitmentSpinner>
        {!vehicleFitmentLoading &&
          vehicleFitmentResults?.vehicles?.length > 0 &&
          // Don't show the fitment details if a group is not selected.
          selectedGroup?.id !== 0 && (
            <VehicleFitmentTable>
              <HeaderRow>
                <MakeColHeader>
                  <I18n name="make" />
                </MakeColHeader>
                <YearsColHeader>
                  <I18n name="years" />
                </YearsColHeader>
                <TotalColHeader>
                  <I18n name="totalApplications" />
                </TotalColHeader>
              </HeaderRow>
              {/* Assumption: The endpoint returns an array of vehicles because
                it supports multiple vehicle's fitment checks.
                Since we only check one vehicle at a time it is safe to take
                only the first element.
            */}
              {vehicleFitmentResults?.vehicles[0]?.makes?.map(
                (vehicle, index) => (
                  <VehicleMakes key={index} vehicle={vehicle} />
                )
              )}
            </VehicleFitmentTable>
          )}
      </>
    )
  }
)

const FitmentSpinner = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const HeaderRow = styled.div`
  background-color: #000;
  color: #fff;
  height: 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 16px;
  text-align: left;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    height: 60px;
  }
`
const VehicleFitmentTable = styled.div`
  min-width: 450px;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    overflow-x: scroll;
  }
`
interface HeaderRowProps {
  width?: string
}
const ColHeader = styled.div<HeaderRowProps>`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  align-items: center;
  font-size: 18px;
  line-height: 27px;
  width: ${(p) => p.width};
  color: #ffffff;
  mix-blend-mode: normal;
  padding-right: 24px;
  padding-left: 24px;
`

const MakeColHeader = styled(ColHeader)`
  width: 40%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 50%;
  }
`
const YearsColHeader = styled(ColHeader)`
  width: 20%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 20%;
  }
`
const TotalColHeader = styled(ColHeader)`
  width: 40%;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 30%;
  }
`

const SelectsWrapper = styled.div`
  display: flex;
  padding: 0;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    flex-direction: column;
    padding: 25px;
  }
`
const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 31px;
`
const SelectsLabels = styled.p`
  font-size: 14px;
  margin: 16px 0px 3px 0px;
  font-family: Roboto;
  font-weight: bold;
  text-align: left;
`
